<template>
    <div class="lx-online-booking-checkout-rental-agreement pt-4 pb-4">
        <card :headerClass="['w-100']" v-if="!client.is_signed">
            <template #header-icon>
                <font-awesome-icon :icon="['fas', 'file-signature']" size="lg" />
            </template>
            <template #header-content> Rental Agreement </template>
            <template #body>
                <div v-if="!rentalAgreementBusy">
                    <b-row class="mb-4">
                        <b-col
                            class="
                                lx-online-booking-checkout-rental-agreement__html
                            "
                            v-html="rentalAgreementHtml"
                        ></b-col>
                    </b-row>
                    <card class="mb-3" nested>
                        <template #header-content> Sign and Accept </template>
                    </card>
                    <b-row>
                        <b-col cols="12">
                            <label><b>Guest IP:</b></label>
                        </b-col>
                        <b-col cols="12"> {{ guestIP }} </b-col>
                    </b-row>
                    <b-row class="mt-3">
                        <b-col cols="12">
                            <label><b>Date and Time Stamp:</b></label>
                        </b-col>
                        <b-col cols="12">
                            {{ signedTimestamp }}
                        </b-col>
                    </b-row>
                    <b-row class="mt-3" no-gutters v-if="rentalAgreement">
                        <b-col cols="12">
                            <label><b>Guest Signature:</b></label>
                        </b-col>
                        <b-col cols="12" v-if="!client.is_signed">
                            <div
                                class="
                                    lx-online-booking-checkout-rental-agreement__sign-here
                                    lx-online-booking-checkout-rental-agreement__sign-here-top
                                "
                            >
                                Sign Here
                            </div>
                        </b-col>
                        <b-col cols="auto" v-if="!client.is_signed">
                            <validation-provider
                                name="signature"
                                rules="required"
                                v-slot="{ errors, handleChange }"
                                
                            >
                                <form-input
                                    size="md"
                                    v-model="client.signature"
                                    floatingLabel="Enter your full name"
                                    invalidFeedback=""
                                    :error="errors[0]"
                                    @input="handleChange"
                                />
                            </validation-provider>
                        </b-col>
                        <b-col cols="auto" v-if="!client.is_signed">
                            <b-button
                                class="
                                    ml-2
                                    lx-online-booking-checkout-rental-agreement__accept-button
                                "
                                variant="primary"
                                size="md"
                                @click="sign"
                                :disabled="!client.signature"
                            >
                                I Accept
                            </b-button>
                        </b-col>
                        <b-col cols="12" v-if="!client.is_signed">
                            <div
                                class="
                                    lx-online-booking-checkout-rental-agreement__sign-here
                                    lx-online-booking-checkout-rental-agreement__sign-here-bottom
                                "
                            >
                                Sign Here
                            </div>
                        </b-col>
                        <b-col v-if="client.is_signed">
                            <span
                                class="
                                    lx-online-booking-checkout-rental-agreement__signature
                                "
                            >
                                {{ client.signature }}
                            </span>
                        </b-col>
                    </b-row>
                    <b-row class="mt-3">
                        <b-col>
                            <p>
                                By clicking the "I Accept" button, you are agreeing to the terms and conditions
                                specified in the rental agreement shown above. All online bookings require a signed
                                rental agreement. Once signed and the booking completed, a signed copy of the rental
                                agreement will be emailed to you and a copy will be saved with your reservation.
                            </p>
                        </b-col>
                    </b-row>
                </div>
                <div v-else>
                    <div class="text-center">
                        <b-spinner
                            class="
                                lx-online-booking-checkout-rental-agreement__spinner
                            "
                            variant="primary"
                        />
                    </div>
                </div>
            </template>
        </card>
        <card :headerClass="['w-100']" v-else>
            <template #header-icon>
                <font-awesome-icon :icon="['fas', 'check']" size="lg" />
            </template>
            <template #header-content>Signing Successful</template>
            <template #body>
                A signed PDF of the agreement has been emailed to you and to the property manager and a copy has been
                saved with your reservation.
            </template>
        </card>
        <modal id="rental-agreement-modal" title="DOCUMENT SIGNATURE REQUIRED" ok-only>
            Please read the rental agreement and sign the document at the bottom of the page.
        </modal>
    </div>
</template>

<script>
    import format from 'date-fns/format';
    import Card from '../generic/card/card.vue';
    import FormInput from '../generic/form-input/form-input.vue';
    import api from '../../api';
    import Modal from '../generic/modal/modal.vue';

    export default {
        name: 'RentalAgreementSignPage',
        components: { Card, FormInput, Modal },
        data() {
            return {
                rentalAgreementBusy: true,
                rentalAgreement: '',
                client: {
                    signature: '',
                    is_signed: false,
                },
            };
        },
        mounted() {
            this.fetchAgreement();
            this.$bvModal.show('rental-agreement-modal');
        },
        computed: {
            documentId() {
                return this.$route.params.id;
            },
            signedTimestamp() {
                return format(new Date(), 'EEEE, MMMM d, y h:mm a');
            },
            guestIP() {
                return window?.GUEST_IP;
            },
            isGuestApp() {
                return this.$route.href.includes('sign-ga');
            },
            rentalAgreementHtml() {
                let ra = `${this.rentalAgreement}`;
                ra = ra.replace(
                    /\[DS_GUEST_SIGNATURE\]/g,
                    this.client.is_signed
                        ? '<span class="lx-online-booking-checkout-rental-agreement__html__signature">' +
                              this.client.signature +
                              '</span>'
                        : ''
                );
                ra = ra.replace(
                    /\[DS_GUEST_INITIALS\]/g,
                    this.client.is_signed
                        ? '<span class="lx-online-booking-checkout-rental-agreement__html__initials">' +
                              this.getInitials(this.client.signature) +
                              '</span>'
                        : ''
                );
                return ra;
            },
        },
        methods: {
            async fetchAgreement() {
                try {
                    this.rentalAgreementBusy = true;
                    const { data } = await api.get('/app/rental-agreement/documents/' + this.documentId);
                    this.rentalAgreement = data.html;
                } catch {
                    this.errorToast({
                        text: 'Document not found.',
                    });
                } finally {
                    this.rentalAgreementBusy = false;
                }
            },
            async sign() {
                try {
                    this.rentalAgreementBusy = true;
                    await api.post('/app/rental-agreement/documents/' + this.documentId + '/rental-agreements/', {
                        signature: this.client.signature,
                        signed_timestamp: new Date().toISOString(),
                        is_signed: true,
                        is_guest_app: this.isGuestApp,
                    });
                    this.client.is_signed = true;
                } catch {
                    this.errorToast({
                        text: 'Could not sign the rental agreement.',
                    });
                } finally {
                    this.rentalAgreementBusy = false;
                }
            },
            getInitials(name) {
                const initials = name.match(/\b\w/g) || [];
                return ((initials.shift() || '') + (initials.pop() || '')).toUpperCase();
            },
        },
    };
</script>

<style lang="scss" scoped>
    @import '~bootstrap/scss/_functions.scss';
    @import '~bootstrap/scss/_variables.scss';
    @import '~bootstrap/scss/mixins/_breakpoints.scss';

    @font-face {
        font-family: 'Dancing Script';
        font-style: normal;
        font-weight: 400;
        src: local('Dancing Script'), local('DancingScript'),
            url('../online-booking/checkout/rental-agreement/font-dancing-script.woff2') format('woff2');
        unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2212,
            U+2215, U+E0FF, U+EFFD, U+F000;
    }

    .lx-online-booking-checkout-rental-agreement {
        &__sign-here {
            display: inline-block;
            position: relative;
            border-radius: 6px;
            border: 4px solid #9fdff5;
            background: #39c0ed;
            line-height: 38px;
            vertical-align: middle;
            font-weight: bold;
            font-size: 17px;
            color: #fbfbfb;
            text-transform: uppercase;
        }

        &__sign-here:before,
        &__sign-here:after {
            border: solid transparent;
            border-radius: 2px;
            content: ' ';
            height: 0;
            width: 0;
            position: absolute;
            pointer-events: none;
        }

        &__sign-here-top {
            margin: 0 0 24px 0;
            padding: 0 8px;
        }

        &__sign-here-top:after,
        &__sign-here-top:before {
            top: 100%;
            left: 50%;
        }

        &__sign-here-top:after {
            border-color: rgba(221, 255, 51, 0);
            border-top-color: #39c0ed;
            border-width: 16px;
            margin-left: -16px;
        }
        &__sign-here-top:before {
            border-color: rgba(194, 225, 245, 0);
            border-top-color: #9fdff5;
            border-width: 22px;
            margin-left: -22px;
        }

        &__sign-here-bottom {
            margin: 5px 0 0 0;
            padding: 0 8px;
        }

        &__sign-here-bottom:after,
        &__sign-here-bottom:before {
            bottom: 100%;
            left: 50%;
        }

        &__sign-here-bottom:after {
            border-color: rgba(221, 255, 51, 0);
            border-bottom-color: #39c0ed;
            border-width: 16px;
            margin-left: -16px;
        }

        &__sign-here-bottom:before {
            border-color: rgba(194, 225, 245, 0);
            border-bottom-color: #9fdff5;
            border-width: 22px;
            margin-left: -22px;
        }

        &__signature {
            padding-left: 10px;
            font: 40px 'Dancing Script', cursive !important;
            white-space: nowrap !important;
        }

        &__accept-button {
            padding: 8px 12px 8px 12px !important;
            margin-bottom: 17px !important;
        }

        &__spinner {
            width: 3rem !important;
            height: 3rem !important;
        }

        &__reminder {
            color: #dc3545;
            font-size: 80%;
        }
        @media (min-width: 768px) {
            margin: 0 auto;
            max-width: 1200px;
        }
    }

    :deep(*) .lx-online-booking-checkout-rental-agreement {
        &__card-danger.card {
            border: 1px solid #dc3545 !important;
            padding-bottom: 3px !important;
        }

        &__html {
            &__signature,
            &__initials {
                padding-left: 10px;
                font: 40px 'Dancing Script', cursive !important;
                white-space: nowrap !important;
            }

            th {
                padding: 5px !important;
            }
            td {
                padding: 5px !important;
            }

            table.brief_balance_table {
                border-collapse: collapse;
            }
            table.brief_balance_table * {
                width: 6cm;
            }
            table.brief_balance_table td {
                color: white;
                background-color: black;
                padding: 0.1cm 0.2cm 0 0.2cm;
                text-align: center;
                font-size: 12px;
                font-weight: bold;
            }
            table.brief_balance_table .brief_balance_container {
                background-color: white;
                padding: 10px;
                color: black;
            }
            table.brief_balance_table .brief_balance_last_row td {
                padding-bottom: 0.2cm;
            }
            table.brief_balance_table .small {
                font-size: 10px;
            }
            table.brief_balance_table .balance_span {
                font-size: 14px;
            }
        }
    }
</style>
